import { SelectChangeEvent } from "@mui/material";
import { useState, useEffect, useCallback } from "react";
import { Metrics } from "../../types/Metrics";
import { CallLead } from "../../types/CallLead";
import { route, getAxios } from "../../utils/endpoints";
import { User } from "../../model/User";
import { GridSortModel } from "@mui/x-data-grid";
import { fillColumnAccess } from "../../components/LeadGrid/ColumnAccess";
import { Chart } from "../../components/Charts/CustomChart/types";
import { formatDateHyphen } from "../../utils/time";

interface IDashboardTable {
  id: number;
  description: string;
  items: any[];
  quantity: number;
  sortOrder: number;
}

export const useHome = () => {
  const user = new User();

  const hideMetrics = user.HasForbiddenAccess("Can Read Metrics");
  const hideTables = user.HasForbiddenAccess("Can Read Dashboard Tables");

  const columnAccess = fillColumnAccess(user);

  const [dashboards, setDashboards] = useState<IDashboardTable[]>([]);
  const [selectedDashboard, setSelectedDashboard] =
    useState<IDashboardTable | undefined>();
  const [dashboardValue, setDashboardValue] = useState<number>(0);

  const [dealerId, setDealerId] = useState<string>("Select an account");

  const [metrics, setMetrics] = useState<Metrics[]>([]);
  const [isMetricsLoading, setIsMetricsLoading] = useState(true);
  const [isLeadsLoading, setIsLeadsLoading] = useState(true);

  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(0);
  const [orderBy, setOrderBy] = useState("");
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "leadDate", sort: "asc" },
  ]);

  const [chartSources, setChartSources] = useState<Chart[]>([]);
  const [sourceOptions, setSourceOptions] = useState<
    {
      sourceID: number;
      sourceSiteName: string;
      lpSiteAddress: string;
      lpClientID: string;
    }[]
  >([]);
  const [selectedChartOption, setSelectedChartOption] = useState<string>("0");
  const [chartStartDate, setChartStartDate] = useState<Date | null>(null);
  const [chartEndDate, setChartEndDate] = useState<Date | null>(null);

  const hasMetrics = !hideMetrics && metrics.length > 0;
  const hasDashboards = !hideTables && dashboards.length > 0;

  const hasHomeContent = hasMetrics || hasDashboards;

  const isDoneLoading = !isMetricsLoading && !isLeadsLoading;

  const showGlobalEmptyState = isDoneLoading && !hasHomeContent;

  const onPageChange = (newPage: number) => setPage(newPage);
  const onPageSizeChange = (newPageSize: number) => setPageSize(newPageSize);

  const onChangeOrder = (newSortModel: GridSortModel) => {
    const field =
      newSortModel[0].field === "caller" ? "LastName" : newSortModel[0].field;

    setSortModel(newSortModel);
    setOrderBy(`${field} ${newSortModel[0].sort}`);
  };

  const onChange = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value as string;

    if (value === "Select an account") {
      user.removeUserAccount();
      setDealerId(value);
      return;
    }

    user.UserAccount = value;
    setDealerId(value);
  };

  const fetchMetrics = useCallback(async () => {
    setIsMetricsLoading(true);

    try {
      const response = await getAxios(
        route.apiDashboard.metrics,
        "GET",
        undefined,
        { userAccount: user.UserAccount },
      );

      if (Array.isArray(response.data)) {
        setMetrics(response.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsMetricsLoading(false);
    }
  }, [user.UserAccount]);

  const fetchDashboardTables = async () => {
    setIsLeadsLoading(true);

    try {
      const response = await getAxios("/dashboard-tables", "GET");
      const data: IDashboardTable[] = response.data;

      data.forEach((dashboard) => {
        dashboard.items = dashboard.items.filter((i) => i.id !== 0);
        dashboard.quantity = dashboard.items.length;

        // Format phone numbers
        dashboard.items.forEach((item) => {
          if (item.phoneNumber) {
            item.phoneNumber = item.phoneNumber.replace(
              /(\d{3})(\d{3})(\d{4})/,
              "($1)$2-$3",
            );
          }
        });
      });

      setDashboards(data);

      if (data.length) {
        setDashboardValue(data[0].id);
        setSelectedDashboard(data[0]);
      } else {
        setSelectedDashboard(undefined);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLeadsLoading(false);
    }
  };

  const syncSources = async () => {
    try {
      const response = await getAxios("/data-sync/sources", "GET");
      const data = await response.data;

      setSourceOptions(data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchChartSources = async () => {
    try {
      const startDate = chartStartDate
        ? formatDateHyphen(chartStartDate)
        : chartStartDate;
      const endDate = chartEndDate
        ? formatDateHyphen(chartEndDate)
        : chartEndDate;

      const response = await getAxios("/dashboard/charts", "GET", undefined, {
        SourceID: parseInt(selectedChartOption),
        StartDate: startDate,
        EndDate: endDate,
      });
      const data: Chart[] = await response.data;

      setChartSources(data);
    } catch (error) {
      console.error(error);
    }
  };

  const moveToDashboard = (id: number) => {
    setDashboardValue(id);
    setSelectedDashboard(dashboards.find((d) => d.id === id));
  };

  useEffect(() => {
    if (!hideMetrics) fetchMetrics();
    if (!hideTables) fetchDashboardTables();

    syncSources();
    fetchChartSources();
  }, [fetchMetrics, page, pageSize, orderBy]);

  return {
    metrics,
    dashboards,
    selectedDashboard,
    dashboardValue,
    dealerId,
    chartSources,
    sourceOptions,
    selectedChartOption,
    chartStartDate,
    chartEndDate,
    hasMetrics,
    hasDashboards,
    hasHomeContent,
    showGlobalEmptyState,
    isMetricsLoading,
    isLeadsLoading,
    hideMetrics,
    hideTables,
    columnAccess,
    page,
    pageSize,
    sortModel,
    orderBy,
    onPageChange,
    onPageSizeChange,
    onChangeOrder,
    onChange,
    moveToDashboard,
    setSelectedChartOption,
    setChartStartDate,
    setChartEndDate,
    fetchChartSources,
  };
};
